import React, { useState, useEffect, useCallback } from 'react';
import {
  Users,
  UserPlus,
  Pencil,
  ShieldCheck,
  ShieldOff,
  Search,
  Clock,
  BarChart2,
  Calendar,
  Frown,
  Mail,
  RefreshCw,
  X,
  Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';

import api from '../api/axios';
import Modal from '../components/common/Modal';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import { useUserColors } from '../context/UserColorsContext';
import { ColorDot, PersonName } from '../components/common/PersonColor';
import { previewMonthly, avgTemplateHours } from '../constants/monthlyModel';
import { formatAssignedAt } from '../constants/weekendRhythm';
import { SHIFT_TYPES, SHIFT_TYPE_KEYS } from '../constants/shiftTypes';

// ── RoleBadge ─────────────────────────────────────────────────────────────────
function RoleBadge({ role }) {
  const cls =
    role === 'superadmin'
      ? 'badge-superadmin'
      : role === 'admin'
      ? 'badge-admin'
      : 'badge-user';
  return <span className={`badge ${cls}`}>{role}</span>;
}

// ── FilterBar ─────────────────────────────────────────────────────────────────
function FilterBar({ search, onSearch, filterRole, onFilterRole, filterStatus, onFilterStatus }) {
  return (
    <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
      <div className="search-bar" style={{ flex: '1 1 220px' }}>
        <Search size={16} className="search-icon" style={{ color: 'var(--text-muted)' }} />
        <input
          className="form-input"
          placeholder="Search by name or email…"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
        />
      </div>
      <select
        className="form-select"
        style={{ flex: '0 1 140px' }}
        value={filterRole}
        onChange={(e) => onFilterRole(e.target.value)}
      >
        <option value="all">All roles</option>
        <option value="user">User</option>
        <option value="admin">Admin</option>
        <option value="superadmin">Superadmin</option>
      </select>
      <select
        className="form-select"
        style={{ flex: '0 1 140px' }}
        value={filterStatus}
        onChange={(e) => onFilterStatus(e.target.value)}
      >
        <option value="all">All statuses</option>
        <option value="active">Active</option>
        <option value="inactive">Inactive</option>
      </select>
    </div>
  );
}

// ── UserTable ─────────────────────────────────────────────────────────────────
function UserTable({ users, onRowClick, currentUserId }) {
  return (
    <div className="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Status</th>
            <th>Contracted hrs</th>
            <th style={{ textAlign: 'right' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr
              key={u.id}
              style={{ opacity: u.is_active ? 1 : 0.55, cursor: 'pointer' }}
              onClick={() => onRowClick(u)}
            >
              <td>
                <PersonName userId={u.id} name={u.name} style={{ fontWeight: 500 }} />
                {u.id === currentUserId && (
                  <span style={{ marginLeft: 6, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    (you)
                  </span>
                )}
              </td>
              <td style={{ color: 'var(--text-light)' }}>{u.email}</td>
              <td>
                <RoleBadge role={u.role} />
              </td>
              <td>
                <span className={`badge ${u.is_active ? 'badge-success' : 'badge-secondary'}`}>
                  {u.is_active ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                {u.contracted_hours_per_week ? u.contracted_hours_per_week : '—'}
                {u.employment_type === 'minijob' && (
                  <span className="badge badge-info" style={{ marginLeft: '6px', fontSize: '0.68rem' }}>Minijob</span>
                )}
              </td>
              <td>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRowClick(u);
                    }}
                  >
                    <Pencil size={14} />
                    Edit
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── StatsPanel ────────────────────────────────────────────────────────────────
function StatsPanel({ stats }) {
  const items = [
    { label: 'Total hours', value: stats.total_hours, cls: 'stat-icon-primary', Icon: Clock },
    { label: 'Shifts worked', value: stats.shifts_worked, cls: 'stat-icon-success', Icon: BarChart2 },
    { label: 'Vacation days', value: stats.vacation_days, cls: 'stat-icon-warning', Icon: Calendar },
    { label: 'Sick days', value: stats.sick_days, cls: 'stat-icon-danger', Icon: Frown },
  ];

  return (
    <div className="grid-2" style={{ gap: '12px' }}>
      {items.map(({ label, value, cls, Icon }) => (
        <div key={label} className="stat-card">
          <div className={`stat-icon ${cls}`}>
            <Icon size={20} />
          </div>
          <div className="stat-content">
            <p className="stat-label">{label}</p>
            <p className="stat-value">{value}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Shared helpers for recurring blocks ──────────────────────────────────────
const REC_DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const REC_PART_LABELS = {
  full: 'full day',
  morning: 'morning (before 13:00)',
  afternoon: 'afternoon (after 13:00)',
};

function recValidityStr(b) {
  if (b.valid_from && b.valid_until) return ` · ${b.valid_from} – ${b.valid_until}`;
  if (b.valid_from) return ` · from ${b.valid_from}`;
  if (b.valid_until) return ` · until ${b.valid_until}`;
  return '';
}

// ── RecurringBlocksSection ────────────────────────────────────────────────────
function RecurringBlocksSection({ userId, outerSaving }) {
  const DAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

  const [blocks, setBlocks] = useState([]);
  const [loadingRec, setLoadingRec] = useState(true);
  const [form, setForm] = useState({ weekdays: [], part: 'full', valid_from: '', valid_until: '', note: '' });
  const [adding, setAdding] = useState(false);

  const fetchBlocks = useCallback(async () => {
    try {
      const res = await api.get(`/users/${userId}/recurring-blocks`);
      setBlocks(res.data);
    } catch {
      toast.error('Failed to load recurring blocks.');
    } finally {
      setLoadingRec(false);
    }
  }, [userId]);

  useEffect(() => {
    setLoadingRec(true);
    fetchBlocks();
  }, [fetchBlocks]);

  const toggleDay = (idx) => {
    setForm((f) => ({
      ...f,
      weekdays: f.weekdays.includes(idx) ? f.weekdays.filter((d) => d !== idx) : [...f.weekdays, idx],
    }));
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (form.weekdays.length === 0) return;
    setAdding(true);
    try {
      for (const wd of form.weekdays) {
        await api.post(`/users/${userId}/recurring-blocks`, {
          weekday: wd,
          part: form.part,
          valid_from: form.valid_from || null,
          valid_until: form.valid_until || null,
          note: form.note || null,
        });
      }
      toast.success(`${form.weekdays.length === 1 ? 'Block' : 'Blocks'} added.`);
      setForm({ weekdays: [], part: 'full', valid_from: '', valid_until: '', note: '' });
      await fetchBlocks();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add block.');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (blockId) => {
    if (!window.confirm('Remove this recurring block?')) return;
    try {
      await api.delete(`/users/${userId}/recurring-blocks/${blockId}`);
      toast.success('Block removed.');
      setBlocks((prev) => prev.filter((b) => b.id !== blockId));
    } catch {
      toast.error('Failed to remove block.');
    }
  };

  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: '14px', marginTop: '14px' }}>
      <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text)', marginBottom: '10px' }}>
        Recurring weekly block
      </p>
      {loadingRec ? (
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '10px' }}>Loading…</p>
      ) : blocks.length === 0 ? (
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '10px' }}>No recurring blocks set.</p>
      ) : (
        <div style={{ marginBottom: '12px' }}>
          {blocks.map((b) => (
            <div
              key={b.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 0',
                borderBottom: '1px solid var(--border)',
                fontSize: '0.85rem',
              }}
            >
              <span style={{ flex: 1 }}>
                Every {REC_DAY_NAMES[b.weekday]} · {REC_PART_LABELS[b.part]}{recValidityStr(b)}
                {b.note && <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}> · {b.note}</span>}
              </span>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => handleDelete(b.id)}
                style={{ padding: '2px 6px' }}
                disabled={outerSaving}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleAdd} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {/* Weekday chips */}
        <div>
          <p style={{ fontSize: '0.75rem', fontWeight: 500, color: 'var(--text)', marginBottom: '6px' }}>Weekday</p>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {DAYS.map((label, idx) => {
              const selected = form.weekdays.includes(idx);
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => toggleDay(idx)}
                  disabled={adding || outerSaving}
                  style={{
                    padding: '4px 10px',
                    borderRadius: '16px',
                    border: `1.5px solid ${selected ? 'var(--primary)' : 'var(--border)'}`,
                    background: selected ? 'var(--primary-light)' : 'var(--surface)',
                    color: selected ? 'var(--primary)' : 'var(--text-muted)',
                    fontWeight: selected ? 600 : 400,
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Part and dates row */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ marginBottom: 0, flex: '2 1 160px' }}>
            <label className="form-label" style={{ fontSize: '0.75rem' }}>Block type</label>
            <select
              className="form-input"
              value={form.part}
              onChange={(e) => setForm((f) => ({ ...f, part: e.target.value }))}
              disabled={adding || outerSaving}
            >
              <option value="full">Full day</option>
              <option value="morning">Morning (before 13:00)</option>
              <option value="afternoon">Afternoon (after 13:00)</option>
            </select>
          </div>
          <div className="absence-date-row" style={{ display: 'flex', gap: '8px', flex: '1 1 240px', minWidth: 0 }}>
            <div className="form-group" style={{ marginBottom: 0, flex: '1 1 90px', minWidth: 0 }}>
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Valid from</label>
              <input
                className="form-input"
                type="date"
                value={form.valid_from}
                onChange={(e) => setForm((f) => ({ ...f, valid_from: e.target.value }))}
                disabled={adding || outerSaving}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0, flex: '1 1 90px', minWidth: 0 }}>
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Valid until</label>
              <input
                className="form-input"
                type="date"
                value={form.valid_until}
                onChange={(e) => setForm((f) => ({ ...f, valid_until: e.target.value }))}
                disabled={adding || outerSaving}
              />
            </div>
          </div>
        </div>

        {/* Note and submit row */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ marginBottom: 0, flex: '2 1 160px' }}>
            <label className="form-label" style={{ fontSize: '0.75rem' }}>Note (optional)</label>
            <input
              className="form-input"
              type="text"
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              placeholder="e.g. University"
              disabled={adding || outerSaving}
            />
          </div>
          <button
            type="submit"
            className="btn btn-primary btn-sm"
            disabled={adding || outerSaving || form.weekdays.length === 0}
            style={{ marginBottom: 0 }}
          >
            {adding ? 'Adding…' : '+ Add weekly block'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── AbsencesSection ───────────────────────────────────────────────────────────
function AbsencesSection({ userId, outerSaving }) {
  const [absences, setAbsences] = useState([]);
  const [loadingAbs, setLoadingAbs] = useState(true);
  const [addForm, setAddForm] = useState({ start_date: '', end_date: '', note: '' });
  const [adding, setAdding] = useState(false);

  const fetchAbsences = useCallback(async () => {
    try {
      const res = await api.get(`/users/${userId}/absences`);
      setAbsences(res.data);
    } catch {
      toast.error('Failed to load absences.');
    } finally {
      setLoadingAbs(false);
    }
  }, [userId]);

  useEffect(() => {
    setLoadingAbs(true);
    fetchAbsences();
  }, [fetchAbsences]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post(`/users/${userId}/absences`, addForm);
      toast.success('Absence added.');
      setAddForm({ start_date: '', end_date: '', note: '' });
      await fetchAbsences();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add absence.');
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteAbsence = async (absenceId) => {
    if (!window.confirm('Remove this absence?')) return;
    try {
      await api.delete(`/users/${userId}/absences/${absenceId}`);
      toast.success('Absence removed.');
      setAbsences((prev) => prev.filter((a) => a.id !== absenceId));
    } catch {
      toast.error('Failed to remove absence.');
    }
  };

  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: '16px', marginTop: '8px' }}>
      <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text)', marginBottom: '10px' }}>
        Date range absence
      </p>
      {loadingAbs ? (
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '10px' }}>Loading…</p>
      ) : absences.length === 0 ? (
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '10px' }}>No absences recorded.</p>
      ) : (
        <div style={{ marginBottom: '12px' }}>
          {absences.map((a) => (
            <div
              key={a.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 0',
                borderBottom: '1px solid var(--border)',
                fontSize: '0.85rem',
              }}
            >
              <span style={{ flex: 1 }}>
                {a.start_date === a.end_date ? a.start_date : `${a.start_date} – ${a.end_date}`}
              </span>
              {a.note && (
                <span style={{ color: 'var(--text-muted)', fontStyle: 'italic', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {a.note}
                </span>
              )}
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => handleDeleteAbsence(a.id)}
                style={{ padding: '2px 6px' }}
                disabled={outerSaving}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      )}
      <form onSubmit={handleAdd} style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="absence-date-row" style={{ display: 'flex', gap: '8px', flex: '1 1 240px', minWidth: 0 }}>
          <div className="form-group" style={{ marginBottom: 0, flex: '1 1 90px', minWidth: 0 }}>
            <label className="form-label" style={{ fontSize: '0.75rem' }}>From</label>
            <input
              className="form-input"
              type="date"
              value={addForm.start_date}
              onChange={(e) =>
                setAddForm((f) => ({ ...f, start_date: e.target.value, end_date: f.end_date || e.target.value }))
              }
              required
              disabled={adding || outerSaving}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0, flex: '1 1 90px', minWidth: 0 }}>
            <label className="form-label" style={{ fontSize: '0.75rem' }}>To</label>
            <input
              className="form-input"
              type="date"
              value={addForm.end_date}
              onChange={(e) => setAddForm((f) => ({ ...f, end_date: e.target.value }))}
              required
              disabled={adding || outerSaving}
            />
          </div>
        </div>
        <div className="form-group" style={{ marginBottom: 0, flex: '2 1 160px' }}>
          <label className="form-label" style={{ fontSize: '0.75rem' }}>Note (optional)</label>
          <input
            className="form-input"
            type="text"
            value={addForm.note}
            onChange={(e) => setAddForm((f) => ({ ...f, note: e.target.value }))}
            placeholder="e.g. Unpaid leave agreed"
            disabled={adding || outerSaving}
          />
        </div>
        <button
          type="submit"
          className="btn btn-primary btn-sm"
          disabled={adding || outerSaving}
          style={{ marginBottom: 0 }}
        >
          {adding ? 'Adding…' : '+ Add absence'}
        </button>
      </form>

      <RecurringBlocksSection userId={userId} outerSaving={outerSaving} />
      <WeekendRhythmSection userId={userId} outerSaving={outerSaving} />
    </div>
  );
}

// ── WeekendRhythmSection ──────────────────────────────────────────────────────
// System-assigned "every other weekend off" rhythm; admins can only reset it

function WeekendRhythmSection({ userId, outerSaving }) {
  const [info, setInfo] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/users/${userId}`)
      .then((r) => setInfo({ rhythm: r.data.weekend_rhythm_assigned, at: r.data.weekend_rhythm_assigned_at }))
      .catch(() => setInfo({ rhythm: null, at: null }));
  }, [userId]);

  const reset = async () => {
    setBusy(true);
    try {
      await api.post(`/users/${userId}/weekend-rhythm/reset`);
      setInfo({ rhythm: null, at: null });
      toast.success('Weekend rhythm will be reassigned at the next generation.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reset weekend rhythm.');
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  return (
    <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
      <p style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>Weekend rhythm</p>
      {!info ? (
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Loading…</p>
      ) : !info.rhythm ? (
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>No rhythm assigned.</p>
      ) : (
        <>
          <p style={{ fontSize: '0.85rem', marginBottom: '8px' }}>
            Assigned: <strong>Rhythm {info.rhythm}</strong>{info.at ? ` since ${formatAssignedAt(info.at)}` : ''}.
            It also applies when no availability is submitted.
          </p>
          {!confirming ? (
            <button
              type="button"
              className="btn btn-secondary"
              style={{ minHeight: '40px' }}
              onClick={() => setConfirming(true)}
              disabled={outerSaving || busy}
            >
              Reassign weekend rhythm at next generation
            </button>
          ) : (
            <div style={{
              padding: '10px 12px', borderRadius: 'var(--radius)', background: 'var(--primary-xlight)',
              border: '1px solid var(--primary)', fontSize: '0.84rem',
            }}>
              <p style={{ marginBottom: '8px' }}>
                The rhythm is removed now. The next generation of a plan where this person chose
                &quot;the system chooses&quot; assigns Rhythm A or B again.
              </p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button type="button" className="btn btn-primary" style={{ minHeight: '40px' }} onClick={reset} disabled={busy}>
                  {busy ? 'Resetting…' : 'Yes, reassign'}
                </button>
                <button type="button" className="btn btn-secondary" style={{ minHeight: '40px' }} onClick={() => setConfirming(false)} disabled={busy}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── PairingsTab ───────────────────────────────────────────────────────────────
function PairingsTab({ userId, allUsers }) {
  const [pairings, setPairings] = useState([]);
  const [loadingPair, setLoadingPair] = useState(true);
  const [addingType, setAddingType] = useState(null);
  const [selectedOtherId, setSelectedOtherId] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchPairings = useCallback(async () => {
    try {
      const res = await api.get(`/users/${userId}/pairings`);
      setPairings(res.data);
    } catch {
      toast.error('Failed to load pairing constraints.');
    } finally {
      setLoadingPair(false);
    }
  }, [userId]);

  useEffect(() => {
    setLoadingPair(true);
    fetchPairings();
  }, [fetchPairings]);

  const getPeerId = (p) => (p.user_a_id === userId ? p.user_b_id : p.user_a_id);
  const getPeerName = (p) => (p.user_a_id === userId ? p.user_b_name : p.user_a_name);

  const pairedIds = new Set(pairings.map(getPeerId));
  const availableForAdd = (allUsers || []).filter(
    (u) => u.id !== userId && u.is_active && !pairedIds.has(u.id)
  );

  const avoidList = pairings.filter((p) => p.constraint_type === 'avoid');
  const preferList = pairings.filter((p) => p.constraint_type === 'prefer');

  const handleAdd = async (constraintType) => {
    if (!selectedOtherId) return;
    setSaving(true);
    try {
      await api.post(`/users/${userId}/pairings`, {
        other_user_id: parseInt(selectedOtherId, 10),
        constraint_type: constraintType,
      });
      toast.success('Constraint added.');
      setSelectedOtherId('');
      setAddingType(null);
      await fetchPairings();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add constraint.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePairing = async (constraintId) => {
    try {
      await api.delete(`/users/${userId}/pairings/${constraintId}`);
      setPairings((prev) => prev.filter((p) => p.id !== constraintId));
      toast.success('Constraint removed.');
    } catch {
      toast.error('Failed to remove constraint.');
    }
  };

  const renderChipList = (items, chipStyle) =>
    items.length === 0 ? (
      <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>None set.</span>
    ) : (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {items.map((p) => (
          <span
            key={p.id}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '3px 8px',
              borderRadius: '99px',
              fontSize: '0.82rem',
              ...chipStyle,
            }}
          >
            {getPeerName(p)}
            <button
              type="button"
              onClick={() => handleDeletePairing(p.id)}
              disabled={saving}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', lineHeight: 1, display: 'flex' }}
            >
              <X size={11} />
            </button>
          </span>
        ))}
      </div>
    );

  const renderAddControl = (type) =>
    addingType === type ? (
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px', flexWrap: 'wrap' }}>
        <select
          className="form-select"
          style={{ flex: 1 }}
          value={selectedOtherId}
          onChange={(e) => setSelectedOtherId(e.target.value)}
          disabled={saving}
        >
          <option value="">— Select person —</option>
          {availableForAdd.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => handleAdd(type)}
          disabled={saving || !selectedOtherId}
        >
          Add
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => { setAddingType(null); setSelectedOtherId(''); }}
          disabled={saving}
        >
          Cancel
        </button>
      </div>
    ) : (
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        style={{ marginTop: '8px' }}
        onClick={() => { setAddingType(type); setSelectedOtherId(''); }}
        disabled={availableForAdd.length === 0 && addingType !== type}
      >
        + Add
      </button>
    );

  if (loadingPair) return <LoadingSpinner label="Loading pairings…" />;

  return (
    <div style={{ paddingTop: '4px' }}>
      <p style={{ fontSize: '0.83rem', color: 'var(--text-muted)', marginBottom: '16px' }}>
        These constraints influence (but do not override) automatic roster generation. Constraints are symmetric — they appear on both employees' pages.
      </p>
      <div style={{ marginBottom: '20px' }}>
        <p style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '8px' }}>Prefer working with</p>
        {renderChipList(preferList, {
          background: 'var(--primary-xlight)',
          color: 'var(--primary-dark)',
          border: '1px solid var(--primary-light)',
        })}
        {renderAddControl('prefer')}
      </div>
      <div>
        <p style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '8px' }}>Avoid working with</p>
        {renderChipList(avoidList, {
          background: 'var(--danger-light)',
          color: 'var(--danger)',
          border: '1px solid var(--danger-light)',
        })}
        {renderAddControl('avoid')}
      </div>
    </div>
  );
}

// ── OMRolesBadgesInline ───────────────────────────────────────────────────────
function OMRolesBadgesInline({ userId }) {
  const [roles, setRoles] = useState([]);
  useEffect(() => {
    if (!userId) return;
    api.get('/om/roles')
      .then((res) => setRoles((res.data || []).filter((r) => r.is_active && String(r.holder_user_id) === String(userId))))
      .catch(() => {});
  }, [userId]);
  if (!roles.length) return null;
  return (
    <div style={{ marginBottom: '12px' }}>
      <span style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginRight: '6px' }}>Ämter:</span>
      {roles.map((r) => (
        <span key={r.id} className="badge badge-info" style={{ marginRight: '5px' }}>{r.name}</span>
      ))}
    </div>
  );
}

// ── UserEditForm ──────────────────────────────────────────────────────────────
const fmt1 = (x) => (Math.round((Number(x) || 0) * 10) / 10).toLocaleString('en-GB');

function MonthlyHint({ children }) {
  return (
    <p style={{ fontSize: '0.75rem', color: 'var(--primary-dark)', marginTop: '3px', marginBottom: 0, fontWeight: 500 }}>
      {children}
    </p>
  );
}

// ── MinijobYearOverview ───────────────────────────────────────────────────────
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function MinijobYearOverview({ userId }) {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/users/${userId}/minijob-year`, { params: { year } })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId, year]);

  const cell = { padding: '5px 8px', textAlign: 'right', whiteSpace: 'nowrap' };
  const head = { ...cell, fontWeight: 600, color: 'var(--text-muted)', background: 'var(--bg)' };

  return (
    <div style={{ marginTop: '16px', padding: '12px', background: 'var(--primary-xlight, #EBF4FB)', borderRadius: 'var(--radius)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
        <p style={{ fontSize: '0.82rem', fontWeight: 600, margin: 0 }}>Minijob year overview</p>
        <select className="form-select" style={{ width: 'auto', padding: '4px 28px 4px 8px', fontSize: '0.8rem' }}
          value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {[thisYear - 1, thisYear, thisYear + 1].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      {loading ? (
        <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: 0 }}>Loading…</p>
      ) : !data ? (
        <p style={{ fontSize: '0.8rem', color: 'var(--danger)', margin: 0 }}>Could not load the year overview.</p>
      ) : (
        <>
          <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--surface, #fff)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
              <thead>
                <tr>
                  <th style={{ ...head, textAlign: 'left' }}>Month</th>
                  <th style={head}>Worked</th>
                  <th style={head}>Planned</th>
                  <th style={head}>Total</th>
                </tr>
              </thead>
              <tbody>
                {data.months.map((m, i) => {
                  const total = m.worked_hours + m.planned_hours;
                  const over = data.monthly_cap > 0 && total > data.monthly_cap;
                  return (
                    <tr key={m.month} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ ...cell, textAlign: 'left' }}>{MONTH_SHORT[i]}</td>
                      <td style={cell}>{m.worked_hours ? `${fmt1(m.worked_hours)} h` : '—'}</td>
                      <td style={cell}>{m.planned_hours ? `${fmt1(m.planned_hours)} h` : '—'}</td>
                      <td style={{ ...cell, fontWeight: 500, color: over ? 'var(--warning)' : undefined }}>{total ? `${fmt1(total)} h` : '—'}</td>
                    </tr>
                  );
                })}
                <tr style={{ borderTop: '2px solid var(--border)', fontWeight: 600 }}>
                  <td style={{ ...cell, textAlign: 'left' }}>Total</td>
                  <td style={cell}>{fmt1(data.total_worked)} h</td>
                  <td style={cell}>{fmt1(data.total_planned)} h</td>
                  <td style={cell}>{fmt1(data.total_worked + data.total_planned)} h</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: '0.8rem', margin: '8px 0 0' }}>
            Yearly budget: <strong>{fmt1(data.yearly_budget)} h</strong> ({fmt1(data.monthly_cap)} h × 12) ·{' '}
            Remaining: <strong style={{ color: data.remaining < 0 ? 'var(--danger)' : 'var(--success)' }}>{fmt1(data.remaining)} h</strong>
          </p>
          <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
            Worked = clocked shifts + Amt hours. Planned = assigned roster shifts (incl. events) from today on.
          </p>
        </>
      )}
    </div>
  );
}

// Admin: shift types this person may be scheduled for (stored as exclusions).
// Saved immediately; already assigned future shifts that conflict are listed,
// never unassigned automatically.
function QualificationsEditor({ user }) {
  const [qualified, setQualified] = useState(() => SHIFT_TYPE_KEYS.filter((k) => !(user.not_qualified || []).includes(k)));
  const [conflicts, setConflicts] = useState([]);
  const [saving, setSaving] = useState(false);
  // Current state from the server (the list row may be older than the last save)
  useEffect(() => {
    let cancelled = false;
    setConflicts([]);
    api.get(`/users/${user.id}`)
      .then((res) => { if (!cancelled && res.data?.qualified_shift_types) setQualified(res.data.qualified_shift_types); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user.id]);

  const toggle = async (key) => {
    const next = qualified.includes(key) ? qualified.filter((k) => k !== key) : SHIFT_TYPE_KEYS.filter((k) => k === key || qualified.includes(k));
    const prev = qualified;
    setQualified(next);
    setSaving(true);
    try {
      const res = await api.put(`/users/${user.id}/qualifications`, { qualified: next });
      setQualified(res.data.qualified_shift_types);
      setConflicts(res.data.conflicts || []);
      toast.success('Qualifications saved.');
    } catch (err) {
      setQualified(prev);
      toast.error(err.response?.data?.error || 'Failed to save qualifications.');
    } finally {
      setSaving(false);
    }
  };

  const worksShifts = (user.works_shifts ?? 1) === 1;
  return (
    <div className="form-group">
      <label className="form-label">Qualified for</label>
      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0 0 6px' }}>
        Uncheck shift types this person must not be scheduled for.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px' }}>
        {SHIFT_TYPES.map((t) => (
          <label key={t.key} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.88rem', cursor: 'pointer', minHeight: '32px' }}>
            <input type="checkbox" checked={qualified.includes(t.key)} onChange={() => toggle(t.key)} disabled={saving} />
            {t.label}
          </label>
        ))}
      </div>
      {worksShifts && qualified.length === 0 && (
        <div style={{ marginTop: '8px', padding: '8px 12px', fontSize: '0.8rem', background: 'var(--warning-light)', border: '1px solid var(--warning)', borderRadius: 'var(--radius)' }}>
          ⚠️ This person can't be scheduled for any regular shift.
        </div>
      )}
      {conflicts.length > 0 && (
        <div style={{ marginTop: '8px', padding: '8px 12px', fontSize: '0.8rem', background: 'var(--warning-light)', border: '1px solid var(--warning)', borderRadius: 'var(--radius)' }}>
          <strong>
            {conflicts.length} assigned {conflicts.length === 1 ? 'shift conflicts' : 'shifts conflict'} with this change:
          </strong>
          <ul style={{ margin: '4px 0 6px', paddingLeft: '18px' }}>
            {conflicts.map((c) => (
              <li key={c.slot_id}>
                {format(new Date(`${c.date}T00:00:00`), 'EEE, d MMM yyyy')} · {c.label} {c.start_time.slice(0, 5)}–{c.end_time.slice(0, 5)}
                <span style={{ color: 'var(--text-muted)' }}> · plan {c.period_start} – {c.period_end} ({c.period_status})</span>
              </li>
            ))}
          </ul>
          <span style={{ color: 'var(--text-light)' }}>Nothing was unassigned. </span>
          <Link to={`/roster?user=${user.id}`} style={{ color: 'var(--primary-dark)', fontWeight: 600 }}>Open roster →</Link>
        </div>
      )}
    </div>
  );
}

// Admin: change a person's colour (unique while palette colours are free)
function UserColorPicker({ user }) {
  const { colorOf, refreshColors } = useUserColors();
  const [usage, setUsage] = useState(null);
  const [saving, setSaving] = useState(false);
  const current = (user.color || '').toUpperCase() || null;
  const shown = colorOf(user.id);

  const load = useCallback(() => {
    api.get('/users/color-palette').then((res) => setUsage(res.data)).catch(() => setUsage(null));
  }, []);
  useEffect(() => { load(); }, [load, user.id]);

  const pick = async (c) => {
    if (c.hex === (shown || '').toUpperCase()) return;
    setSaving(true);
    try {
      await api.put(`/users/${user.id}/color`, { color: c.hex });
      toast.success(`Color changed to ${c.name}.`);
      refreshColors();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to change the color.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="form-group">
      <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        Personal color <ColorDot userId={user.id} size={12} />
      </label>
      {!usage ? (
        <div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
      ) : (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {usage.palette.map((c) => {
              const others = c.used_by.filter((u) => u.id !== user.id);
              const isCurrent = c.hex === (shown || current || '').toUpperCase();
              const blocked = others.length > 0 && !usage.all_used;
              return (
                <button
                  key={c.hex}
                  type="button"
                  onClick={() => pick(c)}
                  disabled={saving || blocked || isCurrent}
                  title={`${c.name}${others.length ? ` — used by ${others.map((u) => u.name).join(', ')}` : ''}${isCurrent ? ' (current)' : ''}`}
                  aria-label={`${c.name}${isCurrent ? ' (current)' : blocked ? ' (taken)' : ''}`}
                  style={{
                    width: '28px', height: '28px', borderRadius: '50%', background: c.hex, padding: 0,
                    border: isCurrent ? '3px solid var(--text)' : '2px solid var(--white)',
                    boxShadow: isCurrent ? '0 0 0 2px var(--primary-light)' : '0 0 0 1px var(--border)',
                    opacity: blocked ? 0.25 : 1, cursor: blocked || isCurrent ? 'default' : 'pointer',
                  }}
                />
              );
            })}
          </div>
          <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '6px' }}>
            {usage.all_used
              ? 'All palette colors are in use, so colors can be shared.'
              : 'Faded colors are used by someone else. Changes are saved immediately and logged.'}
          </p>
        </>
      )}
    </div>
  );
}

// "Tip calculator" permission — saved right away (audit-logged on the server).
// Admins and superadmins always have access.
function TipAccessToggle({ user }) {
  const [on, setOn] = useState(user.can_use_tips === 1);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setOn(user.can_use_tips === 1); }, [user.id, user.can_use_tips]);
  const always = user.role === 'admin' || user.role === 'superadmin';
  const toggle = async (value) => {
    setBusy(true);
    setOn(value);
    try {
      await api.put(`/users/${user.id}/tip-access`, { enabled: value });
      user.can_use_tips = value ? 1 : 0; // keep the list row in sync without a reload
      toast.success(value ? 'Tip calculator access granted.' : 'Tip calculator access removed.');
    } catch (err) {
      setOn(!value);
      toast.error(err.response?.data?.error || 'Failed to save.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="form-group">
      <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: always ? 'default' : 'pointer', userSelect: 'none' }}>
        <input type="checkbox" checked={always || on} disabled={always || busy} onChange={(e) => toggle(e.target.checked)}
          style={{ width: '16px', height: '16px', cursor: always ? 'default' : 'pointer' }} />
        <span>
          <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Tip calculator</span>
          <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            {always ? 'Admins always have access.' : 'Can open "Tips" and calculate / save the weekly tip distribution. Saved right away.'}
          </span>
        </span>
      </label>
    </div>
  );
}

function UserEditForm({ user, saving, onSave, onChangeRole, onToggleActive, isSuperAdmin, canToggle }) {
  const [form, setForm] = useState({
    name: user.name,
    email: user.email,
    payroll_number: user.payroll_number || '',
    contracted_hours_per_week: user.contracted_hours_per_week ?? '',
    preferred_shifts_per_week: user.preferred_shifts_per_week ?? '',
    works_shifts: user.works_shifts ?? 1,
    employment_type: user.employment_type || 'regular',
    minijob_max_hours_month: user.minijob_max_hours_month ?? '',
  });
  const [avgLen, setAvgLen] = useState(null);

  // Average template length for the monthly preview (loaded once)
  useEffect(() => {
    let cancelled = false;
    api.get('/shifts/templates')
      .then((res) => { if (!cancelled) setAvgLen(avgTemplateHours(res.data.templates)); })
      .catch(() => { if (!cancelled) setAvgLen(null); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setForm({
      name: user.name,
      email: user.email,
      payroll_number: user.payroll_number || '',
      contracted_hours_per_week: user.contracted_hours_per_week ?? '',
      preferred_shifts_per_week: user.preferred_shifts_per_week ?? '',
      works_shifts: user.works_shifts ?? 1,
      employment_type: user.employment_type || 'regular',
      minijob_max_hours_month: user.minijob_max_hours_month ?? '',
    });
  }, [user.id]); // eslint-disable-line

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const monthly = previewMonthly({
    contracted: form.contracted_hours_per_week,
    preferred: form.preferred_shifts_per_week,
    employmentType: form.employment_type,
    minijobCap: form.minijob_max_hours_month,
    avgLen,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {};
    if (form.name !== user.name) payload.name = form.name;
    if (form.email !== user.email) payload.email = form.email;
    const contracted = parseFloat(form.contracted_hours_per_week);
    if (form.contracted_hours_per_week !== '' && !isNaN(contracted) && contracted !== (user.contracted_hours_per_week ?? '')) {
      payload.contracted_hours_per_week = contracted;
    }
    const preferred = parseFloat(form.preferred_shifts_per_week);
    if (form.preferred_shifts_per_week !== '' && !isNaN(preferred) && preferred !== (user.preferred_shifts_per_week ?? '')) {
      payload.preferred_shifts_per_week = preferred;
    }
    if (form.works_shifts !== (user.works_shifts ?? 1)) {
      payload.works_shifts = form.works_shifts;
    }
    if (form.payroll_number !== (user.payroll_number || '')) {
      payload.payroll_number = form.payroll_number || null;
    }
    const cap = parseFloat(form.minijob_max_hours_month);
    if (form.employment_type === 'minijob' && !(cap > 0)) {
      toast.error('Minijob users need a monthly hours cap.');
      return;
    }
    const newCap = form.employment_type === 'minijob' ? cap : null;
    if (form.employment_type !== (user.employment_type || 'regular') || newCap !== (user.minijob_max_hours_month ?? null)) {
      payload.employment_type = form.employment_type;
      payload.minijob_max_hours_month = newCap;
    }
    if (Object.keys(payload).length === 0) {
      toast('No changes to save.');
      return;
    }
    onSave(payload);
  };

  return (
    <form onSubmit={handleSubmit} style={{ paddingTop: '4px' }}>
      <div className="form-group">
        <label className="form-label">Name</label>
        <input
          className="form-input"
          value={form.name}
          onChange={set('name')}
          required
          disabled={saving}
        />
      </div>
      <div className="form-group">
        <label className="form-label">Email</label>
        <input
          className="form-input"
          type="email"
          value={form.email}
          onChange={set('email')}
          required
          disabled={saving}
        />
      </div>
      <UserColorPicker user={user} />
      <QualificationsEditor user={user} />
      <div className="form-group">
        <label className="form-label">Personalnummer <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
        <input
          className="form-input"
          type="text"
          value={form.payroll_number}
          onChange={set('payroll_number')}
          placeholder="e.g. 12345"
          disabled={saving}
        />
      </div>
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: '16px', marginTop: '4px', marginBottom: '16px' }}>
        <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text)', marginBottom: '12px' }}>
          Contract
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '12px' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Employment type</label>
            <select
              className="form-select"
              value={form.employment_type}
              onChange={set('employment_type')}
              disabled={saving}
            >
              <option value="regular">Regular</option>
              <option value="minijob">Minijob</option>
            </select>
          </div>
          {form.employment_type === 'minijob' && (
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Minijob max hours/month</label>
              <input
                className="form-input"
                type="number"
                min="0"
                step="0.5"
                value={form.minijob_max_hours_month}
                onChange={set('minijob_max_hours_month')}
                placeholder="e.g. 43"
                required
                disabled={saving}
              />
              <MonthlyHint>
                {monthly.capHours > 0 ? `Cap ${fmt1(monthly.capHours)} h/month ≈ ${monthly.capShifts} shifts · events count too` : 'Legal earnings limit — never exceeded by the generator'}
              </MonthlyHint>
            </div>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Contracted hours/week</label>
            <input
              className="form-input"
              type="number"
              min="0"
              step="0.5"
              value={form.contracted_hours_per_week}
              onChange={set('contracted_hours_per_week')}
              placeholder="e.g. 20"
              disabled={saving}
            />
            <MonthlyHint>
              {Number(form.contracted_hours_per_week) > 0
                ? `≈ ${fmt1(monthly.minHours)} h/month minimum`
                : 'No contract minimum. 0 = not set.'}
            </MonthlyHint>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Preferred shifts/week</label>
            <input
              className="form-input"
              type="number"
              min="0"
              step="0.5"
              value={form.preferred_shifts_per_week}
              onChange={set('preferred_shifts_per_week')}
              placeholder="e.g. 3"
              disabled={saving}
            />
            <MonthlyHint>
              {monthly.noValues
                ? 'Not set — without contract hours this person gets no regular shifts.'
                : `Target ${monthly.target} · max ${monthly.emergencyMax} shifts/month${monthly.derivedPreferred ? ' (derived from contract hours)' : ''}`}
            </MonthlyHint>
          </div>
        </div>
        {monthly.contractExceedsPreference && (
          <div
            style={{
              marginTop: '10px', padding: '8px 12px', fontSize: '0.8rem',
              background: 'var(--warning-light)', border: '1px solid var(--warning)', borderRadius: 'var(--radius)',
            }}
          >
            Contract hours exceed this person's preferred shifts. Please check contract or preference.
          </div>
        )}
        <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '8px', marginBottom: 0 }}>
          Monthly values for a full month, based on {fmt1(avgLen || 8)} h average shift length. Partial months and vacation are pro-rated in each plan.
        </p>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginTop: '14px',
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          <input
            type="checkbox"
            checked={form.works_shifts === 1}
            onChange={(e) => setForm((f) => ({ ...f, works_shifts: e.target.checked ? 1 : 0 }))}
            disabled={saving}
            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
          />
          <span>
            <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Works shifts</span>
            <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Included in roster planning, availability requests, and reminders.
            </span>
          </span>
        </label>
        {user.employment_type === 'minijob' && <MinijobYearOverview userId={user.id} />}
      </div>

      <TipAccessToggle user={user} />

      {isSuperAdmin && (
        <div className="form-group">
          <label className="form-label">Role</label>
          <select
            className="form-select"
            value={user.role}
            onChange={(e) => onChangeRole(e.target.value)}
            disabled={saving}
          >
            <option value="user">User</option>
            <option value="admin">Admin</option>
            <option value="superadmin">Superadmin</option>
          </select>
          <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '4px' }}>
            Role changes take effect immediately.
          </p>
        </div>
      )}

      {canToggle && (
        <div
          style={{
            borderTop: '1px solid var(--border)',
            paddingTop: '16px',
            marginTop: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
          }}
        >
          <div>
            <p style={{ fontSize: '0.875rem', fontWeight: 500 }}>
              Account status:{' '}
              <span className={`badge ${user.is_active ? 'badge-success' : 'badge-secondary'}`}>
                {user.is_active ? 'Active' : 'Inactive'}
              </span>
            </p>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '2px' }}>
              {user.is_active
                ? 'Deactivating prevents login. Historical data is preserved.'
                : 'Reactivating restores login access.'}
            </p>
          </div>
          <button
            type="button"
            className={`btn btn-sm ${user.is_active ? 'btn-danger' : 'btn-success'}`}
            onClick={() => onToggleActive(!user.is_active)}
            disabled={saving}
            style={{ flexShrink: 0 }}
          >
            {user.is_active ? (
              <>
                <ShieldOff size={14} />
                Deactivate
              </>
            ) : (
              <>
                <ShieldCheck size={14} />
                Activate
              </>
            )}
          </button>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? (
            <>
              <div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
              Saving…
            </>
          ) : (
            'Save changes'
          )}
        </button>
      </div>
    </form>
  );
}

// ── UserDetailModal ───────────────────────────────────────────────────────────
function UserDetailModal({
  user,
  detailTab,
  onTabChange,
  stats,
  statsLoading,
  saving,
  onSave,
  onChangeRole,
  onToggleActive,
  onClose,
  isSuperAdmin,
  currentUserId,
  allUsers,
}) {
  if (!user) return null;

  const isSelf = user.id === currentUserId;
  const canToggle = !isSelf && user.role !== 'superadmin';

  return (
    <Modal isOpen={!!user} onClose={onClose} title={user.name} size="lg">
      <div className="tabs" style={{ marginBottom: '20px' }}>
        <button
          className={`tab${detailTab === 'details' ? ' active' : ''}`}
          onClick={() => onTabChange('details')}
        >
          Details
        </button>
        <button
          className={`tab${detailTab === 'stats' ? ' active' : ''}`}
          onClick={() => onTabChange('stats')}
        >
          Work Stats
        </button>
        <button
          className={`tab${detailTab === 'pairings' ? ' active' : ''}`}
          onClick={() => onTabChange('pairings')}
        >
          Pairings
        </button>
      </div>

      {detailTab === 'details' && (
        <>
          <OMRolesBadgesInline userId={user.id} />
          <UserEditForm
            user={user}
            saving={saving}
            onSave={onSave}
            onChangeRole={onChangeRole}
            onToggleActive={onToggleActive}
            isSuperAdmin={isSuperAdmin}
            canToggle={canToggle}
          />
          <AbsencesSection userId={user.id} outerSaving={saving} />
        </>
      )}

      {detailTab === 'stats' &&
        (statsLoading ? (
          <LoadingSpinner label="Loading stats…" />
        ) : stats ? (
          <StatsPanel stats={stats} />
        ) : (
          <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '32px 0' }}>
            No stats available yet.
          </p>
        ))}

      {detailTab === 'pairings' && (
        <PairingsTab userId={user.id} allUsers={allUsers} />
      )}
    </Modal>
  );
}

// ── InviteModal ───────────────────────────────────────────────────────────────
function InviteModal({ isOpen, onClose, onInvite, saving, isSuperAdmin }) {
  const [form, setForm] = useState({ email: '', role: 'user' });
  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onInvite(form);
  };

  // Reset form on close
  useEffect(() => {
    if (!isOpen) setForm({ email: '', role: 'user' });
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Invite New Staff" size="sm">
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label">Email address</label>
          <input
            className="form-input"
            type="email"
            placeholder="colleague@example.com"
            value={form.email}
            onChange={set('email')}
            required
            autoFocus
            disabled={saving}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Role</label>
          <select
            className="form-select"
            value={form.role}
            onChange={set('role')}
            disabled={saving}
          >
            <option value="user">User</option>
            {isSuperAdmin && <option value="admin">Admin</option>}
          </select>
        </div>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '16px' }}>
          An invite link valid for 7 days will be sent to this address.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? (
              'Sending…'
            ) : (
              <>
                <UserPlus size={15} />
                Send invite
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── PendingInvitesSection ─────────────────────────────────────────────────────
function PendingInvitesSection({ invites, resending, onResend }) {
  if (invites.length === 0) return null;
  return (
    <div style={{ marginTop: '32px' }}>
      <h2 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text)', marginBottom: '12px' }}>
        Pending Invites ({invites.length})
      </h2>
      <div
        style={{
          background: 'var(--white)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border)',
          overflow: 'hidden',
        }}
      >
        {invites.map((inv, idx) => {
          const expired = new Date(inv.expires_at) < new Date();
          return (
            <div
              key={inv.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                borderBottom: idx < invites.length - 1 ? '1px solid var(--border)' : 'none',
                opacity: expired ? 0.6 : 1,
              }}
            >
              <Mail size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontWeight: 500, fontSize: '0.9rem' }}>{inv.email}</span>
                <span
                  className={`badge ${inv.role === 'admin' ? 'badge-admin' : 'badge-user'}`}
                  style={{ marginLeft: '8px', fontSize: '0.7rem' }}
                >
                  {inv.role}
                </span>
              </div>
              <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                {expired ? 'Expired' : `Expires ${new Date(inv.expires_at).toLocaleDateString()}`}
              </span>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => onResend(inv)}
                disabled={resending === inv.id}
              >
                <RefreshCw size={13} />
                {resending === inv.id ? 'Sending…' : 'Resend'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── UserManagementPage ────────────────────────────────────────────────────────
function UserManagementPage() {
  const { user: currentUser, isSuperAdmin } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const [selectedUser, setSelectedUser] = useState(null);
  const [detailTab, setDetailTab] = useState('details');
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteSaving, setInviteSaving] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [resending, setResending] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const [usersRes, invitesRes] = await Promise.all([
        api.get('/users'),
        api.get('/users/invites'),
      ]);
      setUsers(usersRes.data);
      setPendingInvites(invitesRes.data);
    } catch {
      toast.error('Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const visibleUsers = users.filter((u) => {
    const matchSearch =
      !search ||
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase());
    const matchRole = filterRole === 'all' || u.role === filterRole;
    const matchStatus =
      filterStatus === 'all' ||
      (filterStatus === 'active' && u.is_active) ||
      (filterStatus === 'inactive' && !u.is_active);
    return matchSearch && matchRole && matchStatus;
  });

  const openUserModal = (user) => {
    setSelectedUser(user);
    setDetailTab('details');
    setStats(null);
  };

  const handleTabChange = async (tab) => {
    setDetailTab(tab);
    if (tab === 'stats' && !stats && selectedUser) {
      setStatsLoading(true);
      try {
        const res = await api.get(`/users/${selectedUser.id}/stats`);
        setStats(res.data.stats);
      } catch {
        toast.error('Failed to load stats.');
      } finally {
        setStatsLoading(false);
      }
    }
  };

  const handleSaveUser = async (payload) => {
    setSaving(true);
    try {
      const res = await api.put(`/users/${selectedUser.id}`, payload);
      setUsers((prev) => prev.map((u) => (u.id === res.data.id ? res.data : u)));
      setSelectedUser(res.data);
      toast.success('User updated.');
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.errors?.[0]?.msg ||
        'Failed to update user.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleChangeRole = async (role) => {
    setSaving(true);
    try {
      await api.put(`/users/${selectedUser.id}/role`, { role });
      const updated = { ...selectedUser, role };
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
      setSelectedUser(updated);
      toast.success('Role updated.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to change role.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (activate) => {
    const confirmMsg = activate
      ? `Reactivate ${selectedUser.name}?\n\nThey will be able to log in again.`
      : `Deactivate ${selectedUser.name}?\n\nThey will not be able to log in. Historical data is preserved.`;

    if (!window.confirm(confirmMsg)) return;

    const action = activate ? 'activate' : 'deactivate';
    setSaving(true);
    try {
      await api.put(`/users/${selectedUser.id}/${action}`);
      const updated = { ...selectedUser, is_active: activate ? 1 : 0 };
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
      setSelectedUser(updated);
      toast.success(
        activate ? `${selectedUser.name} reactivated.` : `${selectedUser.name} deactivated.`
      );
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to ${action} user.`);
    } finally {
      setSaving(false);
    }
  };

  const handleInvite = async ({ email, role }) => {
    setInviteSaving(true);
    try {
      await api.post('/users/invite', { email, role });
      toast.success(`Invite sent to ${email}.`);
      setInviteOpen(false);
      const res = await api.get('/users/invites');
      setPendingInvites(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send invite.');
    } finally {
      setInviteSaving(false);
    }
  };

  const handleResendInvite = async (invite) => {
    setResending(invite.id);
    try {
      await api.post(`/users/invites/${invite.id}/resend`);
      toast.success(`Invite resent to ${invite.email}.`);
      const res = await api.get('/users/invites');
      setPendingInvites(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to resend invite.');
    } finally {
      setResending(null);
    }
  };

  const activeCount = users.filter((u) => u.is_active).length;
  const inactiveCount = users.filter((u) => !u.is_active).length;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Team</h1>
          <p className="page-subtitle">Manage staff accounts, roles, and access.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setInviteOpen(true)}>
          <UserPlus size={16} />
          Invite New Staff
        </button>
      </div>

      {!loading && (
        <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginBottom: '12px' }}>
          <strong style={{ color: 'var(--text)' }}>{activeCount}</strong> active
          {inactiveCount > 0 && (
            <>
              {' '}·{' '}
              <strong style={{ color: 'var(--text)' }}>{inactiveCount}</strong> inactive
            </>
          )}
        </p>
      )}

      <FilterBar
        search={search}
        onSearch={setSearch}
        filterRole={filterRole}
        onFilterRole={setFilterRole}
        filterStatus={filterStatus}
        onFilterStatus={setFilterStatus}
      />

      {loading ? (
        <LoadingSpinner label="Loading team…" />
      ) : visibleUsers.length === 0 ? (
        <div className="empty-state">
          <Users className="empty-state-icon" />
          <h3>{users.length === 0 ? 'No team members yet' : 'No results match your filters'}</h3>
          <p>
            {users.length === 0
              ? 'Invite staff to get started.'
              : 'Try adjusting your search or filters.'}
          </p>
        </div>
      ) : (
        <UserTable
          users={visibleUsers}
          onRowClick={openUserModal}
          currentUserId={currentUser.id}
        />
      )}

      <UserDetailModal
        user={selectedUser}
        detailTab={detailTab}
        onTabChange={handleTabChange}
        stats={stats}
        statsLoading={statsLoading}
        saving={saving}
        onSave={handleSaveUser}
        onChangeRole={handleChangeRole}
        onToggleActive={handleToggleActive}
        onClose={() => setSelectedUser(null)}
        isSuperAdmin={isSuperAdmin}
        currentUserId={currentUser.id}
        allUsers={users}
      />

      <PendingInvitesSection
        invites={pendingInvites}
        resending={resending}
        onResend={handleResendInvite}
      />

      <InviteModal
        isOpen={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvite={handleInvite}
        saving={inviteSaving}
        isSuperAdmin={isSuperAdmin}
      />
    </div>
  );
}

export default UserManagementPage;
